import { readonly } from "vue";
import { storeToRefs } from "pinia";

import type { AuthRepository } from "@/domain/Auth/domain/repositories/AuthRepository";
import { HttpAuthRepository } from "@/domain/Auth/infrastructure/repositories/HttpAuthRepository";
import { useAuthStore } from "@/domain/Auth/infrastructure/stores/authStore";

const httpAuthRepository = new HttpAuthRepository();

const useAuth = (repository: AuthRepository = httpAuthRepository) => {
  const authStore = useAuthStore();
  const { isAuthenticated, user } = storeToRefs(authStore);

  const login = async (email: string, password: string) => {
    try {
      const response = await repository.login(email, password);
      authStore.setAuthData(response.data, null);
      await getUserData();
      return response;
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  };

  const logout = () => {
    authStore.clearAuthData();
    location.reload();
  };

  const forgotPassword = async (email: string) => {
    try {
      const response = await repository.forgotPassword(email);
      return response;
    } catch (error) {
      console.error("Forgot password error:", error);
      throw error;
    }
  };

  const setPassword = async (token: string, password: string, password_confirmation: string) => {
    try {
      const response = await repository.setPassword(token, password, password_confirmation);
      return response;
    } catch (error) {
      console.error("Reset password error:", error);
      throw error;
    }
  };

  const acceptInvitation = async (token: string, password: string, password_confirmation: string) => {
    try {
      const response = await repository.acceptInvitation(token, password, password_confirmation);
      return response;
    } catch (error) {
      console.error("Accept invitation error:", error);
      throw error;
    }
  };

  const getInvitationDetail = async (token: string) => {
    try {
      const response = await repository.getInvitationDetail(token);
      return response.data;
    } catch (error) {
      console.error("Invitation detail error:", error);
      throw error;
    }
  };

  const getUserData = async () => {
    try {
      const response = await repository.getUserData();
      const userData = response.data;
      authStore.setAuthData({ token: authStore.token as string, expires_at: authStore.expiresAt as string }, userData);
      return userData;
    } catch (error) {
      console.error("Get user data error:", error);
      throw error;
    }
  };

  return {
    isAuthenticated: readonly(isAuthenticated),
    user: readonly(user),
    login,
    logout,
    forgotPassword,
    acceptInvitation,
    getInvitationDetail,
    setPassword,
    getUserData,
  };
};

export default useAuth;
